import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Lightbulb, Settings, LogOut, Menu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { PageHeader } from '../components/design-system/PageHeader';
import { IdeaCard } from '../components/ideas/IdeaCard';
import { NewIdeaModal } from '../components/ideas/NewIdeaModal';
import { EditIdeaModal } from '../components/ideas/EditIdeaModal';
import { DeleteIdeaModal } from '../components/ideas/DeleteIdeaModal';
import { resumeRouteForSession, resumeRouteForState } from '../lib/resumeRoute';
import { WIZARD_PATHS, wizardUrl } from '../lib/wizardRoutes';
import {
  listOpenIdeas,
  createIdea,
  updateIdea,
  deleteIdea,
  startWizardFromIdea,
  type CapturedIdea,
} from '../lib/capturedIdeas';

export function Ideas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState<CapturedIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<CapturedIdea | null>(null);
  const [deleting, setDeleting] = useState<CapturedIdea | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [workError, setWorkError] = useState<string | null>(null);

  const refresh = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      setIdeas(await listOpenIdeas(user.id));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [user?.id]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleWorkOut = async (idea: CapturedIdea) => {
    if (workingId) return;           // al bezig, voorkom dubbele sessie
    setWorkingId(idea.id);
    setWorkError(null);
    try {
      // Al in bewerking → hervat de bestaande sessie (niet een nieuwe maken)
      if (idea.status === 'in_bewerking' && idea.session_id) {
        const { data: session } = await supabase
          .from('sessions')
          .select('id, state, article_content, selected_title, selected_intro, selected_image_url, titles, intros, images')
          .eq('id', idea.session_id)
          .maybeSingle();
        const route = session ? resumeRouteForSession(session) : resumeRouteForState('idea_approved');
        navigate(wizardUrl(route, idea.session_id));
        return;
      }
      // Nieuw idee → start een wizard-sessie op stap 2
      const sessionId = await startWizardFromIdea(idea);
      navigate(wizardUrl(WIZARD_PATHS.direction, sessionId));
    } catch (err) {
      console.error('Werk uit mislukt:', err);
      setWorkError('Het uitwerken van dit idee is mislukt. Probeer het opnieuw, of neem contact op met support als het blijft gebeuren.');
    } finally {
      setWorkingId(null);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onSignOut={handleSignOut}
      />
      <div className="lg:pl-64">
        <MaintenanceBanner />
        <LowCreditsBanner />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              <div className="flex items-center gap-3 lg:hidden">
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Instellingen"
                >
                  <Settings className="w-6 h-6" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
          <div className="space-y-6">
          <PageHeader
            title="Ideeën"
            subtitle="Je invallen, klaar om uit te werken"
            actions={
              <button
                onClick={() => setShowNew(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Nieuw idee
              </button>
            }
          />

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : ideas.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
              <Lightbulb className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-[#231F20] mb-2">Nog geen ideeën</h2>
              <p className="text-gray-600">
                Stuur een voice note naar dibbi of voeg er hier handmatig één toe.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {ideas.map(idea => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  onEdit={setEditing}
                  onDelete={setDeleting}
                  onWorkOut={handleWorkOut}
                />
              ))}
            </div>
          )}
          </div>
        </main>
      </div>

      {workError && (
        <div className="fixed bottom-8 right-8 bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 max-w-md z-50">
          <p className="text-sm text-red-600">{workError}</p>
        </div>
      )}
      {showNew && (
        <NewIdeaModal
          onClose={() => setShowNew(false)}
          onSave={async (t, c) => {
            await createIdea(user.id, 'handmatig', t, c);
            setShowNew(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <EditIdeaModal
          idea={editing}
          onClose={() => setEditing(null)}
          onSave={async (id, t, c) => {
            await updateIdea(id, t, c);
            setEditing(null);
            await refresh();
          }}
        />
      )}
      {deleting && (
        <DeleteIdeaModal
          idea={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={async (id) => {
            await deleteIdea(id);
            setDeleting(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}
